/**
 * Table of Contents Styles
 */

import styled from 'styled-components';
import { motion } from 'framer-motion';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';

export const TOCBackdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 50;
`;

export const TOCPanel = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 85vw;
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 51;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const TOCHeader = styled.div`
  padding: ${spacing.xl} ${spacing.xl} ${spacing.lg};
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TOCTitle = styled.h2`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.medium};
  color: white;
  margin: 0;
`;

export const TOCCloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.md};
  border: none;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  font-size: ${typography.fontSize.lg};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: white;
  }
`;

// ── Appendix Accordion ──

export const TOCAccordionHeader = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${spacing.lg} ${spacing.xl} ${spacing.sm};
  border: none;
  background: ${props => props.$active ? `${colors.accent.coral.dark}14` : 'transparent'};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const TOCAccordionLabel = styled.span<{ $active?: boolean }>`
  font-family: ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.$active ? colors.accent.coral.dark : 'rgba(255, 255, 255, 0.3)'};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  transition: color 0.2s ease;
`;

export const TOCChevron = styled(motion.span)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${borderRadius.sm};
  color: rgba(255, 255, 255, 0.3);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.6);
  }
`;

// ── Download Button ──

export const TOCDownloadSection = styled.div`
  margin-top: auto;
  padding: ${spacing.lg} ${spacing.xl};
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

export const TOCDownloadButton = styled.button`
  width: 100%;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm};

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TOCList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TOCItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.md} ${spacing.xl};
  border: none;
  background: ${props => props.$active ? `${colors.accent.coral.dark}14` : 'transparent'};
  color: ${props => props.$active ? colors.accent.coral.dark : 'rgba(255, 255, 255, 0.7)'};
  font-family: ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: white;
  }
`;
